import React, { useState } from 'react';
import { Database, RefreshCw, XCircle, CheckCircle, AlertCircle, Upload, Ban } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';

const WIDGET_AGENTS = ['Transcript Analysis Agent (Jamba)'];
const WIDGET_PROMPT = `Extract structured claim data from this transcript for CRM entry.

REQUIRED FIELDS: customer_name, account_number, transaction_date,
transaction_amount, merchant_name, transaction_channel

OPTIONAL FIELDS: authorization_status, fraud_type, claim_narrative,
supporting_evidence_offered, contact_preferences, account_access_issues,
scam_indicators

Mark each field as: "captured", "missing", or "unclear"

OUTPUT FORMAT (JSON):
{
  "required_fields": { "field_name": { "value": "...", "status": "captured" } },
  "optional_fields": { ... },
  "completeness_score": 85,
  "missing_critical_info": [...],
  "ready_for_crm": false
}`;

const STATUS_ICON = {
  captured: <CheckCircle size={12} className="text-green-500 shrink-0" />,
  missing: <AlertCircle size={12} className="text-red-400 shrink-0" />,
  unclear: <AlertCircle size={12} className="text-yellow-500 shrink-0" />
};

const STATUS_TEXT = {
  captured: 'text-green-700',
  missing: 'text-red-500 italic',
  unclear: 'text-yellow-600 italic'
};

const FieldRow = ({ label, field }) => {
  if (!field) return null;
  const displayValue = field.value != null
    ? (Array.isArray(field.value) ? field.value.join(', ') : String(field.value))
    : '—';
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 gap-2">
      <span className="text-xs text-gray-500 capitalize w-36 shrink-0">
        {label.replace(/_/g, ' ')}
      </span>
      <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
        <span className={`text-xs truncate ${STATUS_TEXT[field.status] || 'text-gray-700'}`}>
          {displayValue}
        </span>
        {STATUS_ICON[field.status]}
      </div>
    </div>
  );
};

const CRMUpdater = ({ result, isLoading, error, onRefresh, onPushToCRM, isPushing }) => {
  const [showOptional, setShowOptional] = useState(false);
  const notApplicable = result?.not_applicable === true;
  const crmData = result?.data;
  const required = crmData?.required_fields || {};
  const optional = crmData?.optional_fields || {};
  const completeness = crmData?.completeness_score ?? 0;
  const readyForCRM = crmData?.ready_for_crm ?? false;
  const missingCritical = crmData?.missing_critical_info || [];
  const hasData = !!crmData;

  const completenessColor = completeness >= 75 ? 'bg-green-500' :
    completeness >= 50 ? 'bg-yellow-500' : 'bg-red-400';

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-violet-50 to-purple-50">
        <div className="flex items-center gap-2">
          <Database size={15} className="text-violet-500" />
          <h3 className="font-semibold text-gray-800 text-sm">CRM Auto-Updater</h3>
          <span className="text-xs text-gray-400 bg-white/60 px-1.5 py-0.5 rounded">Real-time</span>
          {isLoading && hasData && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" title="Updating..." />}
        </div>
        <div className="flex items-center gap-1">
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40"
            title="Re-extract"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="p-4">
        {notApplicable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            <Ban size={22} className="text-gray-300" />
            <p className="text-xs mt-1 text-gray-500">No CRM data to extract — not a fraud claim call</p>
          </div>
        )}

        {!hasData && !notApplicable && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-violet-300 border-t-transparent rounded-full animate-spin" />
              : <Database size={22} className="opacity-30" />}
            <p className="text-xs mt-1">{isLoading ? 'Extracting claim data...' : 'Waiting for transcript data...'}</p>
          </div>
        )}

        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {hasData && (
          <>
            {/* Completeness */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Completeness</span>
                <span className="text-xs font-bold text-gray-700">{completeness}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={`${completenessColor} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${completeness}%` }}
                />
              </div>
            </div>

            {/* Missing critical */}
            {missingCritical.length > 0 && (
              <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-xs font-semibold text-red-700 mb-1">Missing Critical Info</p>
                <div className="flex flex-wrap gap-1">
                  {missingCritical.map((item, i) => (
                    <span key={i} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded capitalize">
                      {item.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Required Fields */}
            <div className="mb-2">
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Required Fields</p>
              <div className="bg-gray-50 rounded-lg px-3 py-1">
                {Object.entries(required).map(([key, field]) => (
                  <FieldRow key={key} label={key} field={field} />
                ))}
              </div>
            </div>

            {/* Optional Fields Toggle */}
            <button
              className="text-xs text-primary font-medium hover:opacity-80 transition-opacity mb-2"
              onClick={() => setShowOptional(!showOptional)}
            >
              {showOptional ? 'Hide' : 'Show'} optional fields ({Object.keys(optional).length})
            </button>

            {showOptional && (
              <div className="mb-3">
                <div className="bg-gray-50 rounded-lg px-3 py-1">
                  {Object.entries(optional).map(([key, field]) => (
                    <FieldRow key={key} label={key} field={field} />
                  ))}
                </div>
              </div>
            )}

            {/* Push to CRM */}
            <button
              onClick={onPushToCRM}
              disabled={!readyForCRM || isPushing}
              className={`w-full mt-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                readyForCRM
                  ? 'bg-primary text-white hover:opacity-90 cursor-pointer'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Upload size={14} className={isPushing ? 'animate-bounce' : ''} />
              {isPushing ? 'Pushing...' : readyForCRM ? 'Push to CRM' : 'Complete Required Fields First'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CRMUpdater;
